import { useQuery } from '@apollo/client/react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { initialize } from '@iterable/web-sdk/dist/authorization';
import { track } from '@iterable/web-sdk/dist/events';
import { DateTimeFormatter, ZonedDateTime, ZoneId } from '@js-joda/core';
import { IterableInitDocument } from 'graphql/types';
import { useCallback, useEffect, useState } from 'react';
import { IterableEvent } from 'utils/iterable';

const ITERABLE_FORMAT = DateTimeFormatter.ofPattern(
  "yyyy-MM-dd'T'HH:mm:ss.SSSZZ",
);

export function formatIterableDate(
  zdt: ZonedDateTime | null | undefined,
): string | undefined {
  if (zdt instanceof ZonedDateTime) {
    return zdt.withZoneSameInstant(ZoneId.UTC).format(ITERABLE_FORMAT);
  }
  return undefined;
}

const useIterable = () => {
  const [isIterableInitialized, setisIterableInitialized] = useState(false);

  if (!process.env.REACT_APP_ITERABLE_KEY) {
    datadogLogs.logger.warn('No Iterable API Key found');
  }

  const {
    data: selfData,
    refetch,
    error: iterableError,
  } = useQuery(IterableInitDocument);

  useEffect(() => {
    if (iterableError) {
      datadogRum.addError(iterableError);
    }
  }, [iterableError]);

  const getJWT = useCallback(async () => {
    try {
      const { data } = await refetch();
      const token = data?.self?.iterableToken?.accessToken;
      if (!token) {
        datadogLogs.logger.warn('No Iterable token found');
      }
      return token || '';
    } catch (e) {
      datadogRum.addError(e);
      throw e;
    }
  }, [refetch]);

  const iterableTrack = useCallback(
    (eventName: string, dataFields?: IterableEvent) => {
      try {
        datadogRum.addAction('Iterable track', { eventName, dataFields });
        track({
          eventName,
          dataFields: { ...dataFields },
        });
      } catch (e) {
        datadogRum.addError(e);
      }
    },
    [],
  );

  useEffect(() => {
    if (selfData?.self?.email) {
      try {
        const { setEmail } = initialize(
          process.env.REACT_APP_ITERABLE_KEY || '',
          () => getJWT().then((response) => response),
        );
        setEmail(selfData?.self?.email).then(() => {
          setisIterableInitialized(true);
        });
      } catch (e) {
        datadogRum.addError(e);
      }
    }
  }, [selfData?.self?.email, getJWT]);

  return {
    isIterableInitialized,
    iterableTrack,
  };
};

export default useIterable;
